/**
 * Cloudflare Turnstile widget (the low-friction CAPTCHA alternative).
 *
 * Renders nothing unless REACT_APP_TURNSTILE_SITE_KEY is set (CRA bakes it in
 * at build time), so the whole feature is dormant until the key exists —
 * same enable-by-env pattern as Resend/GHL on the server. The server side
 * (server/lib/spamGuard.js) independently no-ops until TURNSTILE_SECRET_KEY
 * is set; enable the secret only after a build with the site key is live so
 * real visitors always have a widget to solve.
 *
 * Usage:
 *   const tsRef = useRef(null);
 *   <Turnstile ref={tsRef} onToken={setToken} />
 *   // token is '' until solved; call tsRef.current?.reset() after a failed
 *   // submit — tokens are single-use and expire after ~5 minutes.
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

export function turnstileEnabled() {
  return Boolean(SITE_KEY);
}

// Load the Turnstile script once per page, on first use.
let scriptPromise = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile);
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

const Turnstile = forwardRef(function Turnstile({ onToken }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {
          /* widget already gone — nothing to reset */
        }
      }
      onTokenRef.current('');
    },
  }));

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => onTokenRef.current(''),
        });
      })
      .catch(() => {
        // Script blocked/unreachable: leave the token empty. The server fails
        // open on its side only for Cloudflare outages; a blocked script with
        // enforcement on will surface the server's retry message on submit.
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* already removed */
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} style={{ marginTop: '1rem' }} />;
});

export default Turnstile;
